<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const useCases = [
  {
    icon: 'ri-drop-line',
    color: 'error',
    title: 'Bahan Tumpah atau Rusak',
    example: 'Galon susu jatuh dan tumpah 500ml → kurangi stok susu 500',
  },
  {
    icon: 'ri-gift-2-line',
    color: 'warning',
    title: 'Penggunaan Internal / Tester',
    example: 'Buat 5 cup kopi untuk rapat internal → kurangi kopi 100g, gula 25g',
  },
  {
    icon: 'ri-error-warning-line',
    color: 'error',
    title: 'Kesalahan Input Sebelumnya',
    example: 'Salah catat stok awal kopi: harusnya 5.000g, tapi diisi 500g → tambah 4.500',
  },
  {
    icon: 'ri-truck-line',
    color: 'success',
    title: 'Barang Bonus dari Supplier',
    example: 'Supplier beri bonus 200g kopi di luar PO → tambah stok kopi 200',
  },
  {
    icon: 'ri-arrow-left-right-line',
    color: 'info',
    title: 'Transfer Antar Gudang (Manual)',
    example: 'Pindahkan 1.000g gula dari gudang ke area produksi — jika tidak ada modul transfer',
  },
  {
    icon: 'ri-time-line',
    color: 'warning',
    title: 'Bahan Kadaluarsa / Dibuang',
    example: 'Susu UHT expired 3 botol (600ml) → kurangi stok susu 600',
  },
]

const faq = [
  {
    q: 'Apakah Stock Adjustment bisa dibatalkan setelah disimpan?',
    a: 'Tidak bisa. Setelah diklik "Simpan Adjustment", perubahan stok langsung diterapkan dan tercatat permanen di Riwayat Pergerakan Stok. Jika Anda melakukan kesalahan, buat adjustment baru dengan arah berlawanan untuk mengkoreksi. Contoh: salah kurangi 100g, buat adjustment baru +100g.',
  },
  {
    q: 'Apa bedanya Stock Adjustment dengan Stock Opname?',
    a: 'Stock Adjustment digunakan untuk koreksi cepat satu atau beberapa bahan baku, biasanya untuk kejadian spesifik (tumpah, rusak, bonus). Stock Opname adalah proses menghitung SEMUA bahan baku secara fisik sekaligus, lalu sistem menyesuaikan semua selisih yang ditemukan. Untuk kejadian sehari-hari, pakai Adjustment. Untuk audit bulanan menyeluruh, pakai Opname.',
  },
  {
    q: 'Berapa angka yang harus diisi di kolom "Jumlah Adjustment"?',
    a: 'Isi angka SELISIHNYA, bukan stok akhir yang diinginkan. Contoh: stok saat ini 2.000g, ingin jadi 1.700g → isi -300 (minus 300). Jangan isi 1.700. Sistem yang menghitung stok akhir secara otomatis dan menampilkan preview.',
  },
  {
    q: 'Apakah karyawan kasir bisa melakukan Stock Adjustment?',
    a: 'Bergantung pada pengaturan role di toko Anda. Secara default, hanya akun dengan role Manager atau Admin yang punya akses. Pemilik toko bisa mengatur ini di menu Sistem → User & Role. Disarankan agar akses Stock Adjustment dibatasi untuk mencegah manipulasi stok.',
  },
  {
    q: 'Kolom "Harga Satuan" wajib diisi atau tidak?',
    a: 'Tidak wajib. Jika dikosongkan, sistem akan menggunakan harga rata-rata bahan baku tersebut untuk perhitungan HPP. Isi harga satuan hanya jika Anda tahu persis harga beli bahan yang sedang di-adjust, misalnya untuk bonus dari supplier dengan harga tertentu.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink
            to="/userguide"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink
            to="/userguide/produk-inventory"
            class="text-decoration-none text-medium-emphasis text-body-2"
          >
            Produk &amp; Inventory
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Stock Adjustment</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="warning" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-equalizer-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Stock Adjustment</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Koreksi stok bahan baku secara manual untuk kejadian di luar penjualan normal
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong>
          Memandu Anda melakukan koreksi stok bahan baku secara manual —
          misalnya saat bahan tumpah, kadaluarsa, dipakai internal, atau ada selisih yang perlu dikoreksi.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="warning" />
            Apa itu Stock Adjustment dan Kapan Dipakai?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Di dunia nyata, tidak semua perubahan stok disebabkan oleh penjualan atau pembelian.
              Terkadang susu tumpah, bahan kadaluarsa, atau Anda memakai bahan untuk keperluan
              internal seperti rapat atau foto produk. Inilah fungsi <strong>Stock Adjustment</strong> —
              alat koreksi stok untuk kejadian di luar transaksi normal.
            </p>

            <p class="text-body-2 mb-4">
              Anggap Stock Adjustment sebagai <em>"memo koreksi"</em> di buku stok Anda.
              Setiap koreksi dicatat lengkap dengan alasannya sehingga Anda bisa melacaknya nanti.
            </p>

            <VAlert type="error" variant="tonal" density="compact">
              <strong>⚠️ Peringatan:</strong>
              Stock Adjustment adalah tindakan yang tidak bisa dibatalkan.
              Pastikan angka yang Anda isi sudah benar sebelum menekan tombol Simpan.
            </VAlert>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Kapan Digunakan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-question-line" class="me-2" color="info" />
            Kapan Harus Menggunakan Stock Adjustment?
          </VCardTitle>
          <VCardText>
            <VRow>
              <VCol
                v-for="item in useCases"
                :key="item.title"
                cols="12"
                md="6"
              >
                <VCard variant="outlined" class="h-100">
                  <VCardText class="pa-3">
                    <div class="d-flex align-center gap-2 mb-2">
                      <VAvatar :color="item.color" variant="tonal" size="32" rounded>
                        <VIcon :icon="item.icon" size="16" />
                      </VAvatar>
                      <p class="text-body-2 font-weight-bold mb-0">{{ item.title }}</p>
                    </div>
                    <p class="text-caption text-medium-emphasis mb-0 ps-10">
                      {{ item.example }}
                    </p>
                  </VCardText>
                </VCard>
              </VCol>
            </VRow>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Pakai -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-map-pin-2-line" class="me-2" color="success" />
            Cara Melakukan Stock Adjustment
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4 mb-0">
              <li class="mb-4">
                Buka menu <strong>Inventory → Stock Adjustment</strong> di sidebar kiri
              </li>
              <li class="mb-4">
                Di bagian <strong>"1. Pilih Bahan Baku"</strong>, ketik nama bahan yang ingin dikoreksi.
                Contoh: ketik "susu" maka muncul daftar bahan yang mengandung kata "susu".
                Klik bahan yang tepat.
                <VAlert type="info" variant="tonal" density="compact" class="mt-2">
                  Setelah bahan dipilih, sistem otomatis menampilkan
                  <strong>stok saat ini</strong> dan <strong>harga rata-rata</strong>
                  bahan tersebut. Gunakan info ini sebagai referensi sebelum isi jumlah koreksi.
                </VAlert>
              </li>
              <li class="mb-4">
                Di bagian <strong>"2. Detail Adjustment"</strong>, isi kolom berikut:
                <VTable class="mt-2 mb-2">
                  <thead>
                    <tr>
                      <th style="min-width: 160px">Field</th>
                      <th style="min-width: 80px">Wajib?</th>
                      <th>Penjelasan &amp; Contoh</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Jumlah Adjustment</strong></td>
                      <td>
                        <VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Angka SELISIH yang ingin dikoreksi — bukan stok akhir.
                        <br>Isi angka <span class="text-success font-weight-bold">positif (+)</span>
                        untuk <strong>menambah</strong> stok.
                        <br>Isi angka <span class="text-error font-weight-bold">negatif (−)</span>
                        untuk <strong>mengurangi</strong> stok.
                        <br><br>
                        <em>Contoh 1: Susu tumpah 300ml → isi <span class="text-error font-weight-bold">-300</span></em>
                        <br>
                        <em>Contoh 2: Bonus gula 500g dari supplier → isi <span class="text-success font-weight-bold">+500</span></em>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Harga Satuan</strong></td>
                      <td>
                        <VChip color="success" size="x-small" variant="tonal">❌ Opsional</VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Harga beli per satuan untuk bahan yang di-adjust.
                        Kosongkan jika tidak tahu — sistem akan pakai harga rata-rata.
                        <br>Contoh: <em>Rp 150</em> (per gram kopi bonus dari supplier)
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Alasan Adjustment</strong></td>
                      <td>
                        <VChip color="error" size="x-small" variant="tonal">✅ Wajib</VChip>
                      </td>
                      <td class="text-body-2 py-2">
                        Penjelasan singkat kenapa stok dikoreksi.
                        Isi sejelas mungkin — berguna untuk audit nanti.
                        <br>Contoh: <em>"Susu tumpah saat karyawan baru cuci peralatan, 5 Juni 2025"</em>
                        <br>
                        <span class="text-error text-caption">Maksimal 500 karakter.</span>
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </li>
              <li class="mb-4">
                Perhatikan <strong>preview perubahan</strong> yang muncul di bawah form.
                Sistem menampilkan: stok sekarang → stok setelah adjustment.
                <VAlert type="success" variant="tonal" density="compact" class="mt-2">
                  Contoh preview: <em>"Stok Susu UHT akan menjadi 1.700 ml"</em>
                  (dari sebelumnya 2.000 ml, dikurangi 300 ml).
                  Pastikan angka ini sesuai harapan Anda.
                </VAlert>
              </li>
              <li class="mb-0">
                Klik <strong>"Simpan Adjustment"</strong>.
                Sistem langsung memperbarui stok dan mencatat perubahan ini
                di halaman Riwayat Pergerakan Stok.
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Peringatan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="error" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-alert-line" class="me-2" />
            ⚠️ Hal Penting yang Wajib Diperhatikan
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap text-error">
                  <strong>Tidak bisa dibatalkan.</strong>
                  Setelah disimpan, adjustment tidak bisa dihapus.
                  Jika salah, buat adjustment baru dengan angka berlawanan.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap text-error">
                  <strong>Isi selisih, bukan stok akhir.</strong>
                  Jika stok sekarang 2.000g dan ingin jadi 1.700g, isi <strong>−300</strong>,
                  bukan 1.700.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-close-circle-line">
                <VListItemTitle class="text-body-2 text-wrap text-error">
                  <strong>Alasan wajib diisi sejelas mungkin.</strong>
                  Alasan yang tidak jelas akan menyulitkan audit di kemudian hari.
                  Hindari alasan seperti "koreksi" tanpa penjelasan lebih lanjut.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tips -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard color="warning" variant="tonal">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-lightbulb-line" class="me-2" />
            💡 Tips Praktis
          </VCardTitle>
          <VCardText>
            <VList density="compact">
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Lakukan adjustment sesegera mungkin.</strong>
                  Jangan tunggu sampai akhir minggu. Semakin cepat dikoreksi,
                  semakin akurat laporan harian Anda.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Batasi akses Stock Adjustment.</strong>
                  Hanya manager atau owner yang seharusnya punya akses ini.
                  Terlalu banyak orang bisa adjustment = sulit audit.
                </VListItemTitle>
              </VListItem>
              <VListItem prepend-icon="ri-star-line">
                <VListItemTitle class="text-body-2 text-wrap">
                  <strong>Jika sering adjustment untuk bahan yang sama,</strong>
                  pertimbangkan untuk review recipe produk — mungkin ada bahan
                  yang tidak tercatat dengan benar di recipe.
                </VListItemTitle>
              </VListItem>
            </VList>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Pergerakan Stok', sub: 'Lihat riwayat semua koreksi', icon: 'ri-history-line', color: 'secondary', to: '/userguide/produk-inventory/pergerakan-stok' },
              { title: 'Stock Opname', sub: 'Audit stok menyeluruh bulanan', icon: 'ri-clipboard-line', color: 'success', to: '/userguide/produk-inventory/stock-opname' },
              { title: 'User & Role', sub: 'Atur siapa yang boleh adjustment', icon: 'ri-user-settings-line', color: 'error', to: '/userguide/sistem/user-role' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
